/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import isCombinedViewOnlyUser from 'components/auth/utils/isCombinedViewOnlyUser';
import { createEntity } from 'jotai/entity';
import { UserPreferencesObject, UserPreferencesObjectBasic, UserPreferencesObjectV0 } from 'jotai/userPreferences';
import { getDefaultVehicleColor } from 'jotai/vehicleColor';
import { LockedWorkspaceId, SystemWorkspaceId } from 'jotai/workspace';
import { RunType, SanctioningBodyEnum } from 'models/viewModels';

const LATEST: number = 6;
const combinedViewOnly = isCombinedViewOnlyUser();

const systemDefaultSessionWorkspaces: Record<RunType, string> = {
  [RunType.UNKNOWNRUNTYPE]: SystemWorkspaceId.Blank,
  [RunType.PRACTICE]: combinedViewOnly ? LockedWorkspaceId.CombinedView : SystemWorkspaceId.DefaultPractice,
  [RunType.QUALIFYING]: combinedViewOnly ? LockedWorkspaceId.CombinedView : SystemWorkspaceId.DefaultQualifying,
  [RunType.RACE]: combinedViewOnly ? LockedWorkspaceId.CombinedView : SystemWorkspaceId.DefaultRace,
  [RunType.TEST_EVENT]: SystemWorkspaceId.DefaultTest,
};

const systemSanctioningBodyDefaultWorkspaces: Record<SanctioningBodyEnum, string> = {
  [SanctioningBodyEnum.UNKNOWN]: SystemWorkspaceId.Blank,
  [SanctioningBodyEnum.NASCAR]: combinedViewOnly ? LockedWorkspaceId.CombinedView : SystemWorkspaceId.DefaultRace,
  [SanctioningBodyEnum.INDYCAR]: combinedViewOnly ? LockedWorkspaceId.CombinedView : SystemWorkspaceId.DefaultRaceIndy,
  [SanctioningBodyEnum.IMSA]: LockedWorkspaceId.CombinedView,
  [SanctioningBodyEnum.F1]: SystemWorkspaceId.Blank,
};

const v0Tov1 = (v0: UserPreferencesObjectV0): UserPreferencesObjectBasic => {
  const out = structuredClone(v0) as any;
  out.version = 1;
  out.colorMode = v0.colorMode;
  out.defaultSanctioningBody = v0.defaultSanctioningBody;
  out.selectedCompetitorGroups = {};
  out.competitorGroups = createEntity();
  out.competitorGroupVehicles = createEntity();
  return out as UserPreferencesObject;
};

const v1Tov2 = (v1: UserPreferencesObjectBasic): UserPreferencesObjectBasic => {
  const out = structuredClone(v1) as any;
  out.version = 2;
  out.competitorGroupVehicles.allIds.forEach((id: string) => {
    const vehicle = out.competitorGroupVehicles.byId[id];
    vehicle.highlighted = !!vehicle.colorId;
    vehicle.colorId = vehicle.colorId || getDefaultVehicleColor(vehicle.vehicleNumber).id;
  });
  return out as UserPreferencesObject;
};

const v2Tov3 = (v2: UserPreferencesObjectBasic): UserPreferencesObjectBasic => {
  const out: UserPreferencesObject = structuredClone(v2) as any;
  out.version = 3;
  out.autoSaveWorkspace = true;
  return out;
};

const v3Tov4 = (v3: UserPreferencesObjectBasic): UserPreferencesObjectBasic => {
  const out: UserPreferencesObject = structuredClone(v3) as any;
  out.version = 4;
  out.sessionDefaultWorkspaces = { ...systemDefaultSessionWorkspaces };
  return out;
};

const v4Tov5 = (v4: UserPreferencesObjectBasic): UserPreferencesObjectBasic => {
  const out = structuredClone(v4) as any;
  out.version = 5;
  delete out.competitorGroupVehicles;
  delete out.competitorGroups;
  delete out.selectedCompetitorGroups;
  return out as UserPreferencesObject;
};

const v5tov6 = (v5: UserPreferencesObjectBasic): UserPreferencesObjectBasic => {
  const out = structuredClone(v5) as any;
  out.version = 6;
  out.sanctioningBodyDefaultWorkspaces = { ...systemSanctioningBodyDefaultWorkspaces };
  return out as UserPreferencesObject;
};

const upgradePaths: Record<number, (o: UserPreferencesObjectBasic) => UserPreferencesObjectBasic> = {
  1: v1Tov2,
  2: v2Tov3,
  3: v3Tov4,
  4: v4Tov5,
  5: v5tov6,
};

export const upgrade = (o: Object): UserPreferencesObject => {
  let up;

  // v0 is the only version without a "version" property.
  if (!Object.hasOwn(o, 'version')) {
    up = v0Tov1(o as UserPreferencesObjectV0);
  } else {
    up = o as UserPreferencesObjectBasic;
  }

  let version = up.version;
  for (let i = version; i < LATEST; i++) {
    up = upgradePaths[i](up);
  }
  const upgraded = up as UserPreferencesObject;
  if (combinedViewOnly) {
    //In case they were a user that had defaults before
    if (upgraded.sessionDefaultWorkspaces[RunType.PRACTICE] === SystemWorkspaceId.DefaultPractice) {
      upgraded.sessionDefaultWorkspaces[RunType.PRACTICE] = LockedWorkspaceId.CombinedView;
    }
    if (upgraded.sessionDefaultWorkspaces[RunType.QUALIFYING] === SystemWorkspaceId.DefaultQualifying) {
      upgraded.sessionDefaultWorkspaces[RunType.QUALIFYING] = LockedWorkspaceId.CombinedView;
    }
    if (upgraded.sessionDefaultWorkspaces[RunType.RACE] === SystemWorkspaceId.DefaultRace) {
      upgraded.sessionDefaultWorkspaces[RunType.RACE] = LockedWorkspaceId.CombinedView;
    }
  }
  // Keep their default for NASCAR Race if they had non-default
  if (upgraded.sanctioningBodyDefaultWorkspaces[SanctioningBodyEnum.NASCAR] === SystemWorkspaceId.DefaultRace) {
    if (upgraded.sessionDefaultWorkspaces[RunType.RACE] !== SystemWorkspaceId.DefaultRace) {
      upgraded.sanctioningBodyDefaultWorkspaces[SanctioningBodyEnum.NASCAR] =
        upgraded.sessionDefaultWorkspaces[RunType.RACE];
    }
  }
  return upgraded as UserPreferencesObject;
};

export const getUserPreferencesDefault = (): UserPreferencesObject => {
  return {
    version: LATEST,
    colorMode: 'light',
    autoSaveWorkspace: true,
    sessionDefaultWorkspaces: { ...systemDefaultSessionWorkspaces },
    dataFeedMonitor: true,
    sanctioningBodyDefaultWorkspaces: { ...systemSanctioningBodyDefaultWorkspaces },
  };
};
